// Scale + tick maths for ctx.charts. Pure; no DOM.
// KEEP IN SYNC with ./charts-scale.d.mts.
//
// Everything a chart needs to turn numbers into pixels lives here so it can be
// tested without a canvas: nice-number ticks, auto-scaled domains, the linear
// value↔pixel mapping, and band layout for bars.

const EPS = 1e-12;

/**
 * The classic Heckbert "nice number" — round `range` to 1, 2, 5 or 10 × 10^k.
 * `round` picks nearest (for step sizes); otherwise it rounds up (for extents).
 * @param {number} range
 * @param {boolean} round
 */
export function niceNumber(range, round) {
  if (!Number.isFinite(range) || range <= 0) return 0;
  const exponent = Math.floor(Math.log10(range));
  const fraction = range / Math.pow(10, exponent);
  let nice;
  if (round) {
    if (fraction < 1.5) nice = 1;
    else if (fraction < 3) nice = 2;
    else if (fraction < 7) nice = 5;
    else nice = 10;
  } else {
    if (fraction <= 1) nice = 1;
    else if (fraction <= 2) nice = 2;
    else if (fraction <= 5) nice = 5;
    else nice = 10;
  }
  return nice * Math.pow(10, exponent);
}

/** Nice step that yields at most `maxTicks` ticks across [min,max]. */
export function niceStep(min, max, maxTicks) {
  const span = max - min;
  if (!Number.isFinite(span) || span <= 0) return 0;
  const n = Math.max(2, Math.floor(maxTicks) || 2);
  return niceNumber(niceNumber(span, false) / (n - 1), true);
}

/**
 * Ticks inside [min,max] on nice-number boundaries.
 *
 * Each tick is computed as `first + i*step` rather than accumulated with `+=`:
 * accumulating drifts, and a drifted tick prints as `0.30000000000000004`.
 * @returns {number[]}
 */
export function niceTicks(min, max, maxTicks) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (max - min <= EPS) return Number.isFinite(min) ? [min] : [];
  const step = niceStep(min, max, maxTicks);
  if (!(step > 0)) return [];
  const first = Math.ceil(min / step - EPS) * step;
  const count = Math.floor((max - first) / step + EPS) + 1;
  if (count <= 0) return [];
  const out = [];
  for (let i = 0; i < count; i++) {
    const v = first + i * step;
    // Kill float dust so labels format cleanly (0.1+0.2 problems).
    out.push(Math.abs(v) < step * 1e-9 ? 0 : Number(v.toPrecision(12)));
  }
  return out;
}

/**
 * Expand [min,max] outward to nice-number boundaries so the axis ends on a
 * labelled tick instead of a ragged data value.
 * @returns {{ min:number, max:number, step:number }}
 */
export function niceDomain(min, max, maxTicks) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: 0, max: 1, step: 1 };
  if (max - min <= EPS) {
    // Degenerate: a flat series still needs a readable band around it.
    const pad = Math.max(Math.abs(min) * 0.05, 0.5);
    return { min: min - pad, max: max + pad, step: pad };
  }
  const step = niceStep(min, max, maxTicks);
  if (!(step > 0)) return { min, max, step: (max - min) / 2 };
  // `+ 0` collapses -0, which otherwise formats as "-0" on an axis label.
  return {
    min: Math.floor(min / step + EPS) * step + 0,
    max: Math.ceil(max / step - EPS) * step + 0,
    step,
  };
}

/**
 * Data extent of a set of numbers, ignoring null/undefined/NaN (a gap in a
 * line is a gap, not a zero).
 * @param {ReadonlyArray<number|null|undefined>} values
 * @returns {{ min:number, max:number } | null} null when nothing is finite
 */
export function extent(values) {
  let min = Infinity;
  let max = -Infinity;
  for (const v of values || []) {
    if (v == null || typeof v !== "number" || !Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return min === Infinity ? null : { min, max };
}

/** Union of several extents; nulls are skipped. */
export function unionExtent(extents) {
  let min = Infinity;
  let max = -Infinity;
  for (const e of extents || []) {
    if (!e) continue;
    if (e.min < min) min = e.min;
    if (e.max > max) max = e.max;
  }
  return min === Infinity ? null : { min, max };
}

/**
 * Auto-scale a value axis.
 *
 * `includeZero` defaults TRUE for bars and areas (a bar whose baseline is not
 * zero misstates every ratio it draws) and is passed false for line charts,
 * where the interesting variation is often a thin band far from zero.
 *
 * @param {ReadonlyArray<number|null|undefined>} values
 * @param {{ includeZero?: boolean, padding?: number, maxTicks?: number, nice?: boolean,
 *           min?: number, max?: number }} [opts]
 * @returns {{ min:number, max:number, step:number }}
 */
export function autoDomain(values, opts) {
  const o = opts || {};
  const maxTicks = Number.isFinite(o.maxTicks) ? o.maxTicks : 6;
  const e = extent(values);
  let lo = e ? e.min : 0;
  let hi = e ? e.max : 1;

  if (o.includeZero) {
    if (lo > 0) lo = 0;
    if (hi < 0) hi = 0;
  }
  if (Number.isFinite(o.min)) lo = o.min;
  if (Number.isFinite(o.max)) hi = o.max;

  if (hi - lo <= EPS) {
    const pad = Math.max(Math.abs(hi) * 0.1, 1);
    lo -= pad;
    hi += pad;
    if (o.includeZero && lo > 0) lo = 0;
  } else {
    const pad = (hi - lo) * (Number.isFinite(o.padding) ? o.padding : 0.05);
    // Padding must not manufacture a negative floor for all-positive data —
    // "sales dipped below zero" is a lie an axis should never tell.
    const flooredAtZero = o.includeZero && lo === 0;
    if (!flooredAtZero && !Number.isFinite(o.min)) lo -= pad;
    const ceiledAtZero = o.includeZero && hi === 0;
    if (!ceiledAtZero && !Number.isFinite(o.max)) hi += pad;
  }

  // Zero is a floor, not a suggestion. `includeZero` asks for zero to be IN
  // frame; it was never a licence to invent space *past* it when the flag is
  // off, but that is what padding plus nicing did — DRIP's equity curve
  // (325 … 219190) padded to -10618 and niced to -100000, so a quarter of the
  // plot sat below a value the series can never reach. The sign of the DATA
  // decides this, not the flag. Two escapes stay open: a caller-pinned bound
  // is still not negotiable, and an all-zero series keeps its band rather
  // than collapsing to a zero-width domain.
  const dataMin = e ? e.min : 0;
  const dataMax = e ? e.max : 1;
  if (dataMin >= 0 && !Number.isFinite(o.min) && lo < 0 && hi > 0) lo = 0;
  if (dataMax <= 0 && !Number.isFinite(o.max) && hi > 0 && lo < 0) hi = 0;

  if (o.nice === false) return { min: lo, max: hi, step: (hi - lo) / Math.max(1, maxTicks - 1) };
  const d = niceDomain(lo, hi, maxTicks);
  // A fixed bound the caller asked for is not negotiable, even by nicing.
  if (Number.isFinite(o.min)) d.min = o.min;
  if (Number.isFinite(o.max)) d.max = o.max;
  return d;
}

/**
 * Linear value → pixel mapping. `range` is [p0,p1]; for a y-axis pass
 * [bottom, top] so larger values go up the screen.
 * @returns {(v:number)=>number}
 */
export function scaleLinear(domain, range) {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0;
  if (!Number.isFinite(span) || Math.abs(span) < EPS) {
    const mid = (r0 + r1) / 2;
    return () => mid;
  }
  const k = (r1 - r0) / span;
  return (v) => r0 + (v - d0) * k;
}

/** Inverse of {@link scaleLinear} — pixel → value, for crosshair readouts. */
export function invertLinear(domain, range) {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = r1 - r0;
  if (!Number.isFinite(span) || Math.abs(span) < EPS) return () => d0;
  const k = (d1 - d0) / span;
  return (p) => d0 + (p - r0) * k;
}

/**
 * Band layout for categorical axes (bars, box marks).
 *
 * `padInner` is the share of a step given to the gap between bands and
 * `padOuter` the share at each end. The defaults leave bars clearly separate
 * without floating them apart.
 * @param {number} count
 * @param {[number, number]} range [start, end] in px
 * @param {{ padInner?: number, padOuter?: number }} [opts]
 * @returns {{ step:number, bandWidth:number, start:(i:number)=>number, center:(i:number)=>number,
 *             indexAt:(px:number)=>number }}
 */
export function bandScale(count, range, opts) {
  const o = opts || {};
  const n = Math.max(0, Math.floor(count));
  const [r0, r1] = range;
  const width = r1 - r0;
  const padInner = Number.isFinite(o.padInner) ? Math.min(0.9, Math.max(0, o.padInner)) : 0.2;
  const padOuter = Number.isFinite(o.padOuter) ? Math.max(0, o.padOuter) : 0.1;
  if (n === 0) {
    return {
      step: 0,
      bandWidth: 0,
      start: () => r0,
      center: () => r0,
      indexAt: () => -1,
    };
  }
  const step = width / (n - padInner + 2 * padOuter);
  const bandWidth = step * (1 - padInner);
  const origin = r0 + step * padOuter;
  return {
    step,
    bandWidth,
    start: (i) => origin + i * step,
    center: (i) => origin + i * step + bandWidth / 2,
    indexAt: (px) => {
      if (step <= 0) return -1;
      const i = Math.floor((px - origin) / step);
      return i < 0 || i >= n ? -1 : i;
    },
  };
}

/**
 * Sub-band layout for GROUPED bars: split one band into `groups` slots.
 * A 2px visual gap between neighbours is the mark spec; it is applied by the
 * renderer, not baked in here, so the maths stays resolution-independent.
 */
export function groupedBands(bandStart, bandWidth, groups, gapRatio) {
  const g = Math.max(1, Math.floor(groups));
  const gap = Number.isFinite(gapRatio) ? Math.max(0, Math.min(0.5, gapRatio)) : 0.08;
  const slot = bandWidth / g;
  const w = slot * (1 - gap);
  const out = [];
  for (let i = 0; i < g; i++) out.push({ x: bandStart + i * slot + (slot - w) / 2, width: w });
  return out;
}

/**
 * Nearest index in a sorted ascending numeric array. Used by the crosshair,
 * where "which point is the mouse over" must be O(log n) not O(n).
 * @param {ReadonlyArray<number>} sorted
 * @param {number} value
 * @returns {number} -1 when the array is empty
 */
export function nearestIndex(sorted, value) {
  const n = sorted ? sorted.length : 0;
  if (n === 0) return -1;
  let lo = 0;
  let hi = n - 1;
  if (value <= sorted[0]) return 0;
  if (value >= sorted[hi]) return hi;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid] <= value) lo = mid;
    else hi = mid;
  }
  return value - sorted[lo] <= sorted[hi] - value ? lo : hi;
}

/**
 * Decimal places needed so consecutive ticks of `step` read as distinct values.
 * Prevents an axis printing "1, 1, 1" for 1.0/1.5/2.0.
 */
export function tickPrecision(step) {
  if (!Number.isFinite(step) || step <= 0) return 0;
  return Math.max(0, Math.min(12, -Math.floor(Math.log10(step))));
}

/** Format a tick with a thousands separator and just enough precision. */
export function formatTick(value, step) {
  if (!Number.isFinite(value)) return "";
  const p = tickPrecision(step);
  const abs = Math.abs(value);
  if (abs >= 1e9) return (value / 1e9).toFixed(abs >= 1e10 ? 0 : 1) + "B";
  if (abs >= 1e6) return (value / 1e6).toFixed(abs >= 1e7 ? 0 : 1) + "M";
  if (abs >= 1e4) return (value / 1e3).toFixed(abs >= 1e5 ? 0 : 1) + "k";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: p,
    maximumFractionDigits: p,
  });
}
